const lev1 = (a, b) => {
  if (a === b) return 0;
  const la = a.length, lb = b.length;
  if (Math.abs(la - lb) > 2) return 3;
  const prev = new Array(lb + 1);
  for (let j = 0; j <= lb; j++) prev[j] = j;
  for (let i = 1; i <= la; i++) {
    let diag = prev[0];
    prev[0] = i;
    for (let j = 1; j <= lb; j++) {
      const tmp = prev[j];
      prev[j] = Math.min(prev[j] + 1, prev[j - 1] + 1, diag + (a[i - 1] === b[j - 1] ? 0 : 1));
      diag = tmp;
    }
  }
  return prev[lb];
};

export const SYNONYMS = {
  food: ["restaurant", "bakery", "cafe", "café", "pizza", "burger", "dining", "meal"],
  eat: ["restaurant", "food", "dining", "meal"],
  drink: ["coffee", "bar", "wine", "beer", "juice", "drinks"],
  drinks: ["coffee", "bar", "wine", "beer", "juice"],
  workout: ["gym", "fitness", "yoga"],
  exercise: ["gym", "fitness", "yoga"],
  stay: ["lodging", "hotel", "hostel"],
  sleep: ["lodging", "hotel"],
  coffee: ["cafe", "café", "espresso", "latte"],
  spa: ["massage", "wellness", "beauty"],
  haircut: ["salon", "barber", "beauty"],
  // Caribbean / local vocabulary — shoppers type what they say.
  doubles: ["roti", "channa", "food", "snack"],
  roti: ["doubles", "curry", "food"],
  bake: ["shark", "breakfast", "food", "bakery"],
  pholourie: ["snack", "food"],
  snowcone: ["sno cone", "ice", "dessert"],
  parlour: ["grocery", "shop", "snacks"],
  groceries: ["grocery", "supermarket", "market", "food"],
  grocery: ["supermarket", "market", "groceries"],
  supermarket: ["grocery", "groceries", "market"],
  pharmacy: ["chemist", "drugstore", "health", "medicine"],
  chemist: ["pharmacy", "drugstore", "health"],
  medicine: ["pharmacy", "health", "chemist"],
  doctor: ["clinic", "health", "medical"],
  barber: ["haircut", "salon", "grooming"],
  salon: ["haircut", "barber", "beauty", "nails"],
  nails: ["salon", "beauty", "manicure"],
  gym: ["fitness", "workout", "yoga"],
  hardware: ["tools", "building", "paint", "lumber"],
  phone: ["mobile", "electronics", "gadget", "cell"],
  laptop: ["computer", "electronics", "tech"],
  clothes: ["clothing", "fashion", "apparel", "wear"],
  clothing: ["clothes", "fashion", "apparel"],
  shoes: ["footwear", "sneakers", "fashion"],
  kids: ["children", "child", "toys", "baby"],
  pets: ["pet", "dog", "cat", "vet"],
  vet: ["pet", "pets", "animal"],
  car: ["auto", "vehicle", "tyre", "tire", "mechanic"],
  gas: ["fuel", "petrol", "station"],
  cheap: ["discount", "sale", "bargain", "deal"],
  sale: ["discount", "clearance", "deal"],
  lunch: ["food", "meal", "restaurant"],
  dinner: ["food", "meal", "restaurant"],
  breakfast: ["food", "bake", "cafe", "meal"],
  dessert: ["ice cream", "cake", "sweet", "bakery"],
  water: ["drinks", "beverage"],
};

// Plural/simple-stem variants so "shoes" finds "shoe" and "curries" finds "curry".
const variants = (w) => {
  const v = new Set([w]);
  if (w.length > 3 && w.endsWith("s")) v.add(w.slice(0, -1));
  if (w.length > 4 && w.endsWith("es")) v.add(w.slice(0, -2));
  if (w.length > 4 && w.endsWith("ies")) v.add(`${w.slice(0, -3)}y`);
  if (w.length > 3 && !w.endsWith("s")) v.add(`${w}s`);
  return [...v];
};

// True when every query token matches some word in the text (substring, prefix, synonym, or 1-2 typos).
export const STOPWORDS = new Set(["a", "an", "the", "for", "of", "in", "on", "at", "to", "and", "or", "me", "my", "deal", "deals", "near", "any", "some", "with", "best", "good"]);

export const tokenize = (query) => (query || "").toLowerCase().split(/[^a-z0-9$%.]+/)
  .filter((w) => w && !STOPWORDS.has(w));

// How strongly one token matches one word: 1 exact, 0.85 prefix/substring, 0.6 typo/plural, 0 none.
const tokenWordScore = (tok, word) => {
  if (tok === word) return 1;
  if (word.startsWith(tok) || tok.startsWith(word)) return 0.85;
  if (word.includes(tok) && tok.length >= 3) return 0.8;
  if (variants(tok).includes(word)) return 0.7;
  if (tok.length >= 4) {
    const maxDist = tok.length >= 7 ? 2 : 1;
    if (Math.abs(word.length - tok.length) <= maxDist && lev1(word, tok) <= maxDist) return 0.6;
  }
  return 0;
};

// Weighted relevance across a deal's fields, so a title hit always beats a location hit.
export const matchScore = (fields, query) => {
  const tokens = tokenize(query);
  if (!tokens.length) return 0;
  let total = 0;
  for (const tok of tokens) {
    const cands = [tok, ...(SYNONYMS[tok] || []).map((c) => c.toLowerCase())];
    let best = 0;
    for (const [text, weight] of fields) {
      const words = (text || "").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
      for (const c of cands) {
        const isSyn = c !== tok;
        for (const w of words) {
          const sc = tokenWordScore(c, w) * (isSyn ? 0.55 : 1) * weight;
          if (sc > best) best = sc;
        }
      }
    }
    total += best;
  }
  return total / tokens.length;
};

export const fuzzyMatch = (text, query) => {
  const t = text.toLowerCase();
  const words = t.split(/[^a-z0-9]+/).filter(Boolean);
  const tokens = tokenize(query);
  if (!tokens.length) return true;
  return tokens.every((tok) => {
    const candidates = [tok, ...(SYNONYMS[tok] || []), ...variants(tok)];
    return candidates.some((c) => {
      if (t.includes(c)) return true;
      if (c.length < 4) return false;
      const maxDist = c.length >= 7 ? 2 : 1;
      return words.some((w) => Math.abs(w.length - c.length) <= maxDist && lev1(w, c) <= maxDist);
    });
  });
};

// Rescue pass: true when ANY query token matches, used when a strict search finds nothing.
export const looseMatch = (text, query) => {
  const tokens = tokenize(query);
  if (!tokens.length) return true;
  return tokens.some((tok) => fuzzyMatch(text, tok));
};
